import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { documentService } from '../../services/document.service'
import { toastService } from '../../services/toast.service'
import { InputSwitch } from './InputSwitch'
import { InputText } from './InputText'
import { InputTextarea } from './InputTextarea'

interface EmailDocumentDialogProps {
  show: boolean
  onHide: () => void
  documentIds: number[]
  hasArchiveVersion?: boolean
}

const EmailDocumentDialog: React.FC<EmailDocumentDialogProps> = ({ show, onHide, documentIds, hasArchiveVersion = true }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [useArchiveVersion, setUseArchiveVersion] = useState(hasArchiveVersion)
  const [emailAddress, setEmailAddress] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')

  const emailDocuments = async () => {
    setLoading(true)
    try {
      await documentService.emailDocuments(documentIds, emailAddress, emailSubject, emailMessage, useArchiveVersion)
      for (const id of documentIds) {
        void queryClient.invalidateQueries({ queryKey: ['document-history', id] })
      }
      setLoading(false)
      setEmailAddress('')
      setEmailSubject('')
      setEmailMessage('')
      onHide()
      toastService.showSuccess(t('email_sent'))
    } catch (e) {
      setLoading(false)
      const errorMessage = documentIds.length > 1 ? t('error_emailing_documents') : t('error_emailing_document')
      toastService.showError(errorMessage, e)
    }
  }

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{documentIds.length > 1 ? `${t('email_document')}s` : t('email_document')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputText
          title={t('email_address')}
          type="email"
          value={emailAddress}
          onChange={(value) => setEmailAddress(value)}
          placeholder={t('enter_email_address')}
        />
        <InputText title={t('subject')} value={emailSubject} onChange={(value) => setEmailSubject(value)} placeholder={t('enter_subject')} />
        <InputTextarea
          title={t('message')}
          value={emailMessage}
          onChange={(value) => setEmailMessage(value)}
          placeholder={t('enter_message')}
          rows={4}
        />
        <InputSwitch
          title={t('use_archive_version')}
          value={useArchiveVersion}
          onChange={(value) => setUseArchiveVersion(value)}
          disabled={!hasArchiveVersion}
          horizontal={true}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={emailDocuments} disabled={loading || !emailAddress}>
          {loading && <Spinner animation="border" size="sm" className="me-2" />}
          {t('send')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default EmailDocumentDialog
